import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Calculator, CheckCircle, Circle, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GstPendingSection, GstHistorySection } from "@/components/GstReimbursement";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

export default function Accounting() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [gstRows, setGstRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [logGst, setLogGst] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [monthly, gstPending, hist] = await Promise.all([
        api.get("/accounting/tds-monthly"),
        api.get("/accounting/gst-pending"),
        api.get("/accounting/gst-reimbursed-history"),
      ]);
      setRows(monthly.data);
      setGstRows(gstPending.data);
      setHistory(hist.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((acc, r) => {
    acc.gross += Number(r.gross_paid || 0);
    acc.tds += Number(r.tds_withheld || 0);
    acc.net += Number(r.net_paid || 0);
    acc.deposited += r.deposited ? Number(r.tds_withheld || 0) : 0;
    acc.pending += r.deposited ? 0 : Number(r.tds_withheld || 0);
    return acc;
  }, { gross: 0, tds: 0, net: 0, deposited: 0, pending: 0 });
  const gstPendingTotal = gstRows.reduce((s, r) => s + Number(r.gst_pending || 0), 0);

  return (
    <div className="space-y-5" data-testid="accounting-page">
      <div className="min-w-0 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Accounting</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">TDS & GST summary</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">TDS withheld from every creator payment, plus GST invoices awaiting reimbursement.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setLogGst(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="log-gst-invoice-btn">
            <Receipt size={14} className="mr-1.5" /> Log creator GST invoice
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="accounting-stats">
        <StatCard label="Gross paid" value={fmtCur(totals.gross)} tone="slate" testid="stat-gross" />
        <StatCard label="Total TDS withheld" value={fmtCur(totals.tds)} tone="amber" testid="stat-tds" />
        <StatCard label="TDS deposited" value={fmtCur(totals.deposited)} tone="emerald" testid="stat-deposited" />
        <StatCard label="TDS pending" value={fmtCur(totals.pending)} tone="rose" testid="stat-pending" />
        <StatCard label="GST reimbursement pending" value={fmtCur(gstPendingTotal)} tone="indigo" testid="stat-gst-pending" />
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Monthly TDS</div>
        <Card className="border border-slate-200 shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Payments</TableHead>
                  <TableHead className="text-right">Gross paid</TableHead>
                  <TableHead className="text-right">TDS withheld</TableHead>
                  <TableHead className="text-right">Net paid</TableHead>
                  <TableHead>Deposited?</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Deposit date</TableHead>
                  {isAdmin && <TableHead className="text-right w-[120px]">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-slate-500 py-10">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12">
                      <Calculator size={28} className="mx-auto text-slate-300 mb-2" weight="duotone" />
                      <div className="text-sm text-slate-500">No Final Settlement payments yet, so no TDS to show.</div>
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.month} className="hover:bg-slate-50/60" data-testid={`accounting-row-${r.month}`}>
                    <TableCell className="font-semibold text-slate-900">{fmtMonth(r.month)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.payments_count}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-slate-900">{fmtCur(r.gross_paid)}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-amber-700" data-testid={`row-tds-${r.month}`}>{fmtCur(r.tds_withheld)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-emerald-700">{fmtCur(r.net_paid)}</TableCell>
                    <TableCell data-testid={`row-deposited-${r.month}`}>
                      {r.deposited ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                          <CheckCircle size={14} weight="fill" /> Deposited
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                          <Circle size={14} /> Not deposited
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{r.deposit_ref || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.deposit_date || "—"}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setEditing(r)} className="border-slate-300 h-7 text-xs" data-testid={`mark-deposit-btn-${r.month}`}>
                          <Receipt size={12} className="mr-1" /> {r.deposited ? "Edit" : "Mark"}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <GstPendingSection rows={gstRows} loading={loading} isAdmin={isAdmin} onChanged={load} />
      <GstHistorySection items={history} loading={loading} />

      <DepositDialog row={editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <LogGstInvoiceDialog open={logGst} onOpenChange={setLogGst} onSaved={() => { setLogGst(false); load(); }} />
    </div>
  );
}

function LogGstInvoiceDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ creator_id: "", invoice_date: "", gst_amount: "", invoice_number: "", notes: "" });
  const [creators, setCreators] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ creator_id: "", invoice_date: new Date().toISOString().slice(0, 10), gst_amount: "", invoice_number: "", notes: "" });
    // Load every GST creator across all visible campaigns so accounts can pick one
    (async () => {
      try {
        const campsRes = await api.get("/campaigns");
        const camps = campsRes.data || [];
        const all = [];
        for (const c of camps) {
          const r = await api.get(`/campaigns/${c.id}/creators`);
          for (const cr of (r.data || [])) {
            if ((cr.invoice_type || "").toLowerCase() === "gst") {
              all.push({ id: cr.id, username: cr.username, brand: c.brand_name, campaign_id: c.id, final_cost: cr.final_cost || 0 });
            }
          }
        }
        setCreators(all);
      } catch (e) { /* non-fatal */ }
    })();
  }, [open]);

  const submit = async () => {
    if (!form.creator_id) { toast.error("Pick a creator"); return; }
    if (!form.invoice_date) { toast.error("Invoice date required"); return; }
    const amt = parseFloat(form.gst_amount || 0);
    if (!amt || amt <= 0) { toast.error("GST amount required"); return; }
    const picked = creators.find((c) => c.id === form.creator_id);
    setSaving(true);
    try {
      await api.post("/accounting/gst-invoices", {
        ...form, gst_amount: amt,
        campaign_id: picked?.campaign_id || "",
      });
      toast.success("GST invoice logged · reimbursement tracking on");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to log");
    } finally { setSaving(false); }
  };

  const picked = creators.find((c) => c.id === form.creator_id);
  const amt = parseFloat(form.gst_amount || 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="log-gst-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Log a creator GST invoice</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Creator (GST invoice type) *</Label>
            <select
              value={form.creator_id}
              onChange={(e) => setForm((f) => ({ ...f, creator_id: e.target.value }))}
              className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
              data-testid="gst-invoice-creator-select"
            >
              <option value="">Pick a creator…</option>
              {creators.map((c) => (
                <option key={c.id} value={c.id}>@{c.username} · {c.brand}</option>
              ))}
            </select>
            {picked && (
              <div className="text-[11px] text-slate-500 mt-1">Final cost: <span className="font-mono">{fmtCur(picked.final_cost)}</span> · 18% = <span className="font-mono">{fmtCur(picked.final_cost * 0.18)}</span></div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice date *</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))} className="mt-1" data-testid="gst-invoice-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">GST amount (₹) *</Label>
              <Input type="number" value={form.gst_amount} onChange={(e) => setForm((f) => ({ ...f, gst_amount: e.target.value }))} className="mt-1" data-testid="gst-invoice-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice number</Label>
            <Input value={form.invoice_number} onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))} placeholder="e.g. INV-2026-0012" className="mt-1" data-testid="gst-invoice-number" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1" />
          </div>
          {amt > 0 && (
            <div className="p-2 rounded-md border border-indigo-200 bg-indigo-50 text-xs text-indigo-900">
              This logs <b className="font-mono">{fmtCur(amt)}</b> as GST reimbursement pending.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="gst-invoice-save-btn">
            {saving ? "Saving…" : "Log invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, tone = "slate", testid }) {
  const toneCls = {
    slate: "border-slate-200 bg-white",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  }[tone] || "border-slate-200 bg-white";
  return (
    <div className={`p-3 rounded-md border ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="stat-num text-lg sm:text-xl font-semibold mt-0.5 font-mono">{value}</div>
    </div>
  );
}

function DepositDialog({ row, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ deposited: false, deposit_ref: "", deposit_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (row) setForm({
      deposited: !!row.deposited,
      deposit_ref: row.deposit_ref || "",
      deposit_date: row.deposit_date || "",
      notes: row.deposit_notes || "",
    });
  }, [row]);
  const submit = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await api.put(`/accounting/tds-deposit/${row.month}`, form);
      toast.success(form.deposited ? "Marked as deposited" : "Deposit status updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deposit-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">TDS deposit · {fmtMonth(row?.month)}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">TDS due</div>
              <div className="font-mono font-semibold text-amber-800">{fmtCur(row?.tds_withheld)}</div>
            </div>
            <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Payments in month</div>
              <div className="font-mono font-semibold text-slate-900">{row?.payments_count || 0}</div>
            </div>
          </div>
          <label className="flex items-center gap-2 p-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50" data-testid="deposit-toggle">
            <input type="checkbox" checked={form.deposited} onChange={(e) => setForm((f) => ({ ...f, deposited: e.target.checked }))} className="w-4 h-4 accent-emerald-600" />
            <span className="text-sm font-medium text-slate-900">TDS has been deposited to the government</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reference (challan #)</Label>
              <Input value={form.deposit_ref} onChange={(e) => setForm((f) => ({ ...f, deposit_ref: e.target.value }))} placeholder="e.g. 1234567890" className="mt-1" data-testid="deposit-ref-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Deposit date</Label>
              <Input type="date" value={form.deposit_date} onChange={(e) => setForm((f) => ({ ...f, deposit_date: e.target.value }))} className="mt-1" data-testid="deposit-date-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1" data-testid="deposit-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="deposit-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
